import { Option } from "@shared/types/prompt";
import { Checkbox } from "@shared/components/shadcn/checkbox";
import { Label } from "@shared/components/shadcn/label";
import { Input } from "@shared/components/shadcn/input";
import { useState, useRef } from "react";

interface CheckboxOptionProps {
  option: Option;
  isSelected: boolean;
  index: number;
  onChange: (value: string[]) => void;
  value: string[];
  name: string;
  onBlur: () => void;
  onEnterPress?: () => void;
}

export default function CheckboxOption({
  option,
  isSelected,
  index,
  onChange,
  value,
  name,
  onBlur,
  onEnterPress,
}: CheckboxOptionProps) {
  const [customValue, setCustomValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleCheckedChange = (checked: boolean) => {
    if (checked && option.allowCustomInput) {
      // Start with original value, user can replace it
      setCustomValue(option.value);
      const newValues = [...value, option.value];
      onChange(newValues);
    } else if (!checked && option.allowCustomInput) {
      // Remove both the original value and any custom value
      const newValues = value.filter(v => v !== option.value && !v.startsWith(option.value + ": "));
      onChange(newValues);
      setCustomValue("");
    } else {
      // Regular checkbox behavior
      const newValues = checked 
        ? [...value, option.value]
        : value.filter(v => v !== option.value);
      onChange(newValues);
    }
  };

  const handleCustomInputChange = (inputValue: string) => {
    setCustomValue(inputValue);
    
    // Replace the original value with the custom input in the array
    const filteredValues = value.filter(v => v !== option.value && !v.startsWith(option.value + ": "));
    const customAnswer = inputValue.trim() || option.value;
    const newValues = [...filteredValues, customAnswer];
    onChange(newValues);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onEnterPress && customValue.trim()) {
      e.preventDefault();
      onEnterPress();
    }
  };

  return (
    <div className="flex items-center space-x-3 py-3 px-2 sm:py-2 sm:px-0 rounded-lg hover:bg-[#D2CED4]/10 transition-colors duration-200">
      <Checkbox
        id={`checkbox-${index}`}
        checked={isSelected}
        onCheckedChange={handleCheckedChange}
        name={name}
        onBlur={onBlur}
        className="data-[state=checked]:bg-[#2A6354] data-[state=checked]:border-[#2A6354] border-2 border-[#BFBCC0] w-5 h-5 sm:w-4 sm:h-4"
      />
      
      {/* Show input field when selected and allows custom input, otherwise show label */}
      {isSelected && option.allowCustomInput ? (
        <Input
          ref={inputRef}
          key={`checkbox-${index}-${option.value}-custom-input`} // Stable key to prevent re-creation
          value={customValue}
          onChange={(e) => handleCustomInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={option.customInputPlaceholder || "Please specify..."}
          className="flex-1 text-base sm:text-sm bg-transparent border-0 focus:border-0 focus:ring-0 p-0 shadow-none"
          autoFocus
        />
      ) : (
        <Label
          htmlFor={`checkbox-${index}`}
          className="text-base sm:text-sm font-normal leading-relaxed peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer select-none"
        >
          {option.value}
        </Label>
      )}
    </div>
  );
} 